import type { ChatMessage } from '../../services/chatApi';

/**
 * Should the optimistic user bubble (`pendingMsg`) be cleared?
 *
 * Clears as soon as the server has persisted our user message — at that point
 * it renders via the messages list, so the optimistic copy is a redundant
 * duplicate. We deliberately do NOT wait for the assistant reply: the gateway
 * turn can take many seconds (tool calls), and keeping both copies on screen
 * for that whole window shows the user's message twice.
 *
 * No visual gap: the server copy renders the moment this returns true.
 */
export function shouldClearPendingMsg(messages: ChatMessage[], pending: string | null): boolean {
  if (!pending) return false;
  // Once the server has ours, it is the last user message in the list.
  const lastUser = [...messages].reverse().find((m) => m.role === 'user');
  return !!lastUser && lastUser.content === pending;
}

/**
 * Should the thinking/streaming indicator be cleared?
 *
 * The stream lifecycle (onDone/onError/timeout) normally clears it, but the
 * SSE pipe can die mid-turn (proxy reap, network blip) while the server-side
 * turn keeps running. The reply then arrives via WebSocket/poll as a
 * persisted assistant message — and until this hook existed, nothing cleared
 * the spinner until the 5-minute timeout. The transcript is the truth: once
 * an assistant reply lands AFTER this turn started, the turn is over.
 */
export function shouldClearThinking(
  messages: ChatMessage[],
  isThinking: boolean,
  streamStartedAtMs: number
): boolean {
  if (!isThinking || messages.length === 0) return false;
  const last = messages[messages.length - 1];
  if (last.role !== 'assistant') return false;
  return new Date(last.created_at).getTime() > streamStartedAtMs;
}
